import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER, GRAY_LIGHT, NEAR_BLACK, WHITE} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';

interface ApplicationCardProps {
  onClick: () => void;
  titleText: string;
  bodyText: string;
  imagePath: string;
  buttonText: string;
  hoverOn?: () => void;
  hoverOff?: () => void;
  compact?: boolean;
}

const useStyles = createStyles((theme, {compact}: {compact: boolean}) => ({
  container: {
    height: compact ? '148px' : '184px',
    width: '340px',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: '3px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: compact ? 'center' : undefined,
    flexShrink: compact ? 0 : undefined,
    ':hover': {
      boxShadow: '0px 2px 6px rgba(162, 185, 190, 0.7)',
    },
  },
  image: {
    height: compact ? '80px' : '100px',
    width: compact ? '80px' : '100px',
    marginTop: compact ? 0 : '42px',
    marginLeft: compact ? theme.spacing.lg : '21px',
  },
  textBox: {
    marginTop: compact ? 0 : '33px',
    marginLeft: compact ? theme.spacing.xl : theme.spacing.xxl,
    marginRight: theme.spacing.lg,
  },
  titleText: {
    fontSize: '18px',
    lineHeight: '24px',
    color: NEAR_BLACK,
  },
  bodyText: {
    fontSize: '12px',
    color: GRAY_DARKER,
    lineHeight: '14px',
    paddingTop: theme.spacing.sm,
  },
  button: {
    marginTop: theme.spacing.sm,
  },
  hoverButton: {
    backgroundColor: BLUE_LIGHT,
    color: WHITE,
  },
}));

export default function SignupScreenCard(props: ApplicationCardProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();

  const {onClick, titleText, bodyText, imagePath, buttonText, hoverOn, hoverOff, compact} = props;
  const {classes, cx} = useStyles({compact: compact ?? false});

  React.useEffect(() => {
    if (hoverOn && isHovered) {
      hoverOn();
    }
    if (hoverOff && !isHovered) {
      hoverOff();
    }
  }, [isHovered, hoverOn, hoverOff]);

  return (
    <div className={classNames(classes.container, 'u-pointer')} onClick={onClick} {...hoverProps}>
      <span>
        {/* Decorative image; the adjacent title conveys the card's meaning to screen readers. */}
        <img alt="" src={imagePath} className={classes.image} />
      </span>
      <span className={classNames('u-flex-grow', classes.textBox)}>
        <div className={classes.titleText}>{titleText}</div>
        <div className={classes.bodyText}>{bodyText}</div>
        <Button
          className={cx(classes.button, {[classes.hoverButton]: isHovered})}
          text={buttonText}
          buttonStyle={ButtonStyle.TERTIARY_BLUE}
          size={ButtonSize.MEDIUM}
        />
      </span>
    </div>
  );
}
